import MapView from "@arcgis/core/views/MapView";
import { useContext, useEffect, useRef } from "react";
import { MapContext } from "../../../../App/MapContextContainer";
import { DECLARATIONS_MIN_SCALE_TO_SHOW } from "../../../../App/useNotificationsLayer";
import * as projection from "@arcgis/core/geometry/projection";
import { PREFERRED_WKID } from "../../../CustomAreas/UserDataContextContainer";
import Extent from "@arcgis/core/geometry/Extent";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { getLajiFiObservationsFetch } from "./utils";
import { ObservationsContext } from "../../ObservationsContextContainer";
import { FeatureCollection, Point, Polygon } from "@turf/helpers";
import Graphic from "@arcgis/core/Graphic";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import { PageContext } from "../../../../App/PageContextContainer";
import loadingNotifications from "../../../../App/loadingNotifications";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import { useGetObservationPopupLine } from "./useGetObservationPopupLine";
import { getFromAndToDatesFromMonths } from "../../../utils";

const preferredSpatialReference = {
    wkid: PREFERRED_WKID
};

const fillSymbol = new SimpleFillSymbol({
    color: "green",
    style: 'solid',
    outline: {
        color: "green",
        width: 2
    }
});

const clashingSymbol = new SimpleMarkerSymbol({
    color: "green",
    size: "8"
});

const useBboxUpdateForGraphicsLayer = (view: MapView, layer: GraphicsLayer) => {
    const { getObservationLine } = useGetObservationPopupLine();
    const viewWatcher = useRef<__esri.WatchHandle | null>(null);
    const mapContext = useContext(MapContext);
    const observationsContext = useContext(ObservationsContext);
    let abortController = new AbortController();
    const isFetching = useRef(false);
    const pageContext = useContext(PageContext);
    const watcherHandle = useRef<IHandle | null>(null);

    const popupTemplate = new PopupTemplate({
        title: "Lisätietoja lajihavainnosta",
        content: getObservationLine
    });

    const updateBBOX = async () => {
        if (layer.visible === false) {
            return;
        }

        pageContext.setLoadingSomething(loadingNotifications.loadingAllLajiFiObservations);

        isFetching.current = true;

        let geometryInPreferredWkid = projection.project(view.extent, preferredSpatialReference) as Extent;

        let response: Response;

        const { fromDate, toDate } = getFromAndToDatesFromMonths(observationsContext.observationsState.observationMonths);

        try {
            response = await getLajiFiObservationsFetch(geometryInPreferredWkid, {
                "anyHabitat": "MKV.habitatM;MKV.habitatS;MKV.habitatR;MKV.habitatK;MKV.habitatT;MKV.habitatI",
                "taxonId": observationsContext.observationsState.taxonIds,
                "collectionId": observationsContext.observationsState.collectionIds,
                "collectionIdNot": 'http://tun.fi/HR.48' // Exclude Rengastus- ja löytörekisteri (TIPU)
            }, false, abortController?.signal);
        } catch (error) {
            isFetching.current = false;

            return;
        }

        const featureCollection: FeatureCollection<Polygon | Point> = await response.json();

        layer.removeAll();

        const graphics: Graphic[] = [];

        for (const feature of featureCollection.features) {
            if (fromDate && feature.properties && feature.properties["gathering.eventDate.begin"] < fromDate?.toISOString()) {
                continue;
            }

            if (toDate && feature.properties && feature.properties["gathering.eventDate.end"] > toDate?.toISOString()) {
                continue;
            }

            let geometry = {};

            if (feature.geometry.type === "Point") {
                geometry = {
                    latitude: feature.geometry.coordinates[1],
                    longitude: feature.geometry.coordinates[0],
                    spatialReference: { wkid: PREFERRED_WKID },
                    type: "point"
                };

                graphics.push(new Graphic({
                    geometry: geometry,
                    symbol: clashingSymbol,
                    popupTemplate,
                    attributes: feature.properties
                }));
            } else if (feature.geometry.type === "Polygon") {
                geometry = {
                    rings: feature.geometry.coordinates,
                    spatialReference: { wkid: PREFERRED_WKID },
                    type: "polygon"
                };

                graphics.push(new Graphic({
                    geometry: geometry,
                    symbol: fillSymbol,
                    popupTemplate,
                    attributes: feature.properties
                }));
            }
        }

        layer.addMany(graphics);

        isFetching.current = false;
        pageContext.resetLoadingSomething(loadingNotifications.loadingAllLajiFiObservations);
    };

    const handleUpdate = async () => {
        if (view.scale <= DECLARATIONS_MIN_SCALE_TO_SHOW &&
            (observationsContext.observationsState.taxonIds.length > 0 || observationsContext.observationsState.collectionIds.length > 0)) {

            if (isFetching.current) {
                abortController.abort();
                abortController = new AbortController();
            }

            await updateBBOX();
        }
    }

    useEffect(() => {
        if (viewWatcher.current !== null) {
            viewWatcher.current.remove();
        }

        if (watcherHandle.current !== null) {
            watcherHandle.current?.remove();
        }

        watcherHandle.current = reactiveUtils.watch(
            () => layer.visible,
            async (visible) => {
                if (visible) {
                    await handleUpdate();
                }
            });

        viewWatcher.current = view.watch("stationary", async (stationary) => {
            if (stationary) {
                await handleUpdate();
            }
        });

        return () => {
            if (viewWatcher.current !== null) {
                viewWatcher.current.remove();
                watcherHandle.current?.remove();
            }
        }
    }, [mapContext.mapAndViewReady, observationsContext.observationsState.taxonIds, observationsContext.observationsState.collectionIds, observationsContext.observationsState.observationMonths]);

    useEffect(() => {
        handleUpdate();
    }, [observationsContext.observationsState.taxonIds, observationsContext.observationsState.collectionIds, observationsContext.observationsState.observationMonths]);
}

export default useBboxUpdateForGraphicsLayer;